import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CalendarPlus, Send, Copy } from "lucide-react";
import PushSettings from "@/components/PushSettings";

const ALL = [90, 60, 30, 7, 0];

export default function ReminderSettings() {
  const [data, setData] = useState(null);
  const [cal, setCal] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/reminders").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (prefs) => {
    const { data: p } = await api.put("/reminders/prefs", prefs);
    setData({ ...data, prefs: p }); toast.success("Reminder preferences saved");
  };
  const toggleT = (t) => {
    const cur = data.prefs.thresholds; save({ ...data.prefs, thresholds: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t].sort((a, b) => b - a) });
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/reminders/send-now");
      if (r.sent === 0) toast.info(r.detail);
      else if (r.ok) toast.success(`Digest with ${r.sent} items emailed to you`);
      else toast.error("Email could not be delivered right now");
      load();
    } catch { toast.error("Could not send"); } finally { setBusy(false); }
  };
  const getCal = async () => {
    const { data: r } = await api.get("/calendar/link");
    const url = `${API}/calendar/${r.token}.ics`;
    setCal(url); navigator.clipboard?.writeText(url); toast.success("Calendar link copied — paste into Google/Apple Calendar 'Subscribe by URL'");
  };

  if (!data) return null;
  const soon = data.upcoming.filter((u) => u.days_left !== null && u.days_left <= 90).slice(0, 6);
  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:col-span-2" data-testid="reminder-settings">
      <h2 className="font-heading font-bold mb-1 flex items-center gap-2"><Bell size={18} /> Reminders & calendar</h2>
      <p className="text-sm text-muted-foreground mb-4">Daily 8am digest covering certificates, insurance, Right to Rent re-checks, Legionella reviews, registrations and deposit deadlines.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="flex items-center justify-between p-3 rounded-xl border border-border">
            <span className="text-sm font-medium">Email reminders</span>
            <input data-testid="reminder-email-toggle" type="checkbox" checked={data.prefs.email} onChange={(e) => save({ ...data.prefs, email: e.target.checked })} className="h-5 w-5 accent-emerald-600" />
          </label>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-4 mb-2">Remind me at</p>
          <div className="flex flex-wrap gap-2">
            {ALL.map((t) => <button key={t} data-testid={`threshold-${t}`} onClick={() => toggleT(t)} className={`min-h-[36px] px-3 rounded-full text-sm border transition-colors ${data.prefs.thresholds.includes(t) ? "bg-navy text-white border-navy" : "border-border hover:bg-secondary"}`}>{t === 0 ? "Expiry day" : `${t} days`}</button>)}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="send-digest-now" disabled={busy} onClick={sendNow} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold transition-colors disabled:opacity-60"><Send size={16} /> {busy ? "Sending…" : "Email me a digest now"}</button>
            <button data-testid="calendar-subscribe" onClick={getCal} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl border border-border font-medium hover:bg-secondary transition-colors"><CalendarPlus size={16} /> Subscribe in calendar</button>
          </div>
          {cal && <div className="mt-2 flex items-center gap-2 text-xs font-mono bg-secondary rounded-lg px-3 py-2"><span className="truncate" data-testid="calendar-url">{cal}</span><button onClick={() => { navigator.clipboard?.writeText(cal); toast.success("Copied"); }}><Copy size={14} /></button></div>}
          <PushSettings />
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Coming up (next 90 days)</p>
          {soon.length === 0 ? <p className="text-sm text-muted-foreground">Nothing dated within 90 days.</p> : (
            <div className="space-y-1.5" data-testid="upcoming-list">
              {soon.map((u) => <div key={u.key} className="flex items-center justify-between text-sm p-2 rounded-lg bg-secondary/60"><div className="min-w-0"><div className="font-medium truncate">{u.title}</div><div className="text-xs text-muted-foreground truncate">{u.property_name}</div></div><span className={`text-xs font-semibold whitespace-nowrap ${u.days_left < 0 ? "text-rose-600" : u.days_left <= 30 ? "text-amber-600" : "text-emerald-deep"}`}>{u.days_left < 0 ? `${-u.days_left}d overdue` : u.days_left === 0 ? "today" : `${u.days_left}d`}</span></div>)}
            </div>
          )}
          {data.sent.length > 0 && <p className="text-xs text-muted-foreground mt-3" data-testid="last-sent">Last reminder: {data.sent[0].title} · {new Date(data.sent[0].sent_at).toLocaleString()} · {data.sent[0].status}</p>}
        </div>
      </div>
    </div>
  );
}
